/**
 * API-клиент: отзывы, NPS-опросы, типизированные переменные клиента.
 * Контракт — backend/app/api/v1/endpoints/customer_care.py.
 */
import type { AxiosRequestConfig } from 'axios';
import api from './api';

export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface Review {
  id: number;
  order_id: number;
  rating: number;
  text: string | null;
  photo_urls: string[] | null;
  status: ReviewStatus;
  response_text: string | null;
  response_author_alias: string | null;
  created_at: string;
}

export interface ReviewModerateBody {
  status: ReviewStatus;
  response_text?: string | null;
  response_author_alias?: string | null;
}

export async function listAdminReviews(status?: ReviewStatus): Promise<Review[]> {
  const r = await api.get('/admin/reviews', { params: status ? { status } : undefined });
  return r.data;
}

export async function moderateReview(reviewId: number, body: ReviewModerateBody): Promise<Review> {
  const r = await api.patch(`/admin/reviews/${reviewId}`, body);
  return r.data;
}

// ── NPS ──────────────────────────────────────────────────

export type NpsQuestionType = 'choice' | 'range' | 'text';

export interface NpsQuestion {
  id: number;
  text: string;
  question_type: NpsQuestionType;
  choices: string[] | null;
  range_min: number | null;
  range_max: number | null;
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
  version: number;
}

export interface NpsQuestionUpsertBody {
  text: string;
  question_type: NpsQuestionType;
  choices?: string[] | null;
  range_min?: number | null;
  range_max?: number | null;
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
}

export async function listAdminNpsQuestions(): Promise<NpsQuestion[]> {
  const r = await api.get('/admin/nps/questions');
  return r.data;
}

export async function createNpsQuestion(body: NpsQuestionUpsertBody): Promise<NpsQuestion> {
  const r = await api.post('/admin/nps/questions', body);
  return r.data;
}

export async function updateNpsQuestion(id: number, body: NpsQuestionUpsertBody): Promise<NpsQuestion> {
  const r = await api.put(`/admin/nps/questions/${id}`, body);
  return r.data;
}

// ── Typed client variables ──────────────────────────────────

export type ClientVariableValueType = 'string' | 'int' | 'bool' | 'date' | 'enum';

export interface ClientVariableDefinition {
  id: number;
  key: string;
  label: string;
  value_type: ClientVariableValueType;
  enum_choices: string[] | null;
  is_active: boolean;
}

export interface ClientVariableDefinitionCreateBody {
  key: string;
  label: string;
  value_type: ClientVariableValueType;
  enum_choices?: string[] | null;
}

export interface ClientVariableValue {
  definition_id: number;
  key: string;
  value: { v: string | number | boolean };
  version: number;
}

export async function listVariableDefinitions(config?: AxiosRequestConfig): Promise<ClientVariableDefinition[]> {
  const r = await api.get('/admin/client-variables/definitions', config);
  return r.data;
}

export async function createVariableDefinition(
  body: ClientVariableDefinitionCreateBody,
  config?: AxiosRequestConfig,
): Promise<ClientVariableDefinition> {
  const r = await api.post('/admin/client-variables/definitions', body, config);
  return r.data;
}

export async function listClientVariableValues(userId: number, config?: AxiosRequestConfig): Promise<ClientVariableValue[]> {
  const r = await api.get(`/admin/clients/${userId}/variables`, config);
  return r.data;
}

export async function setClientVariableValue(
  userId: number,
  definitionId: number,
  value: string | number | boolean,
  expectedVersion: number,
  config?: AxiosRequestConfig,
): Promise<ClientVariableValue> {
  const r = await api.put(`/admin/clients/${userId}/variables/${definitionId}`, { value, expected_version: expectedVersion }, config);
  return r.data;
}

// ── Personal discount ───────────────────────────────────────
// Контракт версионирован (CAS по expected_version/version) — см. аудит
// D:\claude dev\work\dlvry-zhorka-audit-20261017. До появления ручки на
// сервере запрос вернёт ошибку — в этом случае Save блокируется, чтобы не
// затереть текущее серверное значение неизвестным UI-значением.

export async function getPersonalDiscount(
  userId: number, config?: AxiosRequestConfig,
): Promise<{ personal_discount: number | null; version: number }> {
  const r = await api.get(`/admin/clients/${userId}/personal-discount`, config);
  return r.data;
}

export async function setPersonalDiscount(
  userId: number,
  personalDiscount: number | null,
  expectedVersion: number,
  config?: AxiosRequestConfig,
): Promise<{ ok: boolean; personal_discount: number | null; version: number }> {
  const r = await api.patch(`/admin/clients/${userId}/personal-discount`, {
    personal_discount: personalDiscount, expected_version: expectedVersion,
  }, config);
  return r.data;
}
